import React, { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableColumn, Pager, Text } from "@foris/avocado-suite";
import { Icon, IconTypes } from "@foris/avocado-icons";
import { GroupChange, PageInfo } from "@models/ISchema";
import { getColumnCell, getPillCell, getUserCell } from "@common/utils/table.util";
import scss from "./changeRequestsTable.module.scss";

interface ChangeRequestsTableProps {
  groupChanges: GroupChange[];
  pageInfo: PageInfo;
  requestGroupCapacityChangeDetails: (page: number) => void;
  pageSize: number;
  setColumns: (columns: any[]) => void;
  onChangeSizePerPage: (value: number) => void;
}

/**
 * Displays a paginated table of group changes with type-safe column rendering
 * Handles pagination and communicates with parent component for data fetching
 */
const ChangeRequestsTable: FC<ChangeRequestsTableProps> = ({
  groupChanges,
  pageInfo,
  requestGroupCapacityChangeDetails,
  pageSize,
  setColumns,
  onChangeSizePerPage,
}) => {
  const { t } = useTranslation();
  // Column definitions with type-safe rendering functions
  const columns = [
    {
      key: "date",
      header: t("group-management.table.date"),
      renderCell: (groupChange: GroupChange) => getColumnCell(groupChange?.createdAt),
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "196px" },
      },
    },
    {
      key: "group",
      header: t("group-management.table.group"),
      renderCell: (groupChange: GroupChange) => getColumnCell(groupChange?.group?.code ?? "-"),
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "196px" },
      },
    },
    {
      key: "propertyUpdated",
      header: t("group-management.table.type-change"),
      renderCell: (groupChange: GroupChange) => {
        const prop = groupChange?.propertyUpdated === "capacity" ? "Cupos" : "Visibilidad";
        return getColumnCell(prop);
      },
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "196px" },
      },
    },
    {
      key: "startAndRequired",
      header: t("group-management.table.start-and-required"),
      renderCell: (groupChange: GroupChange) => {
        // Determines appropriate icon based on the change direction
        const getCapacityIcon = (before: string, after: string): IconTypes => {
          if (before < after) return "chevron-up";
          if (before === after) return "minus";
          else return "chevron-down";
        };

        const current = groupChange?.current;
        const expected = groupChange?.expected;

        if (groupChange?.propertyUpdated === "capacity") {
          return (
            <Text className={scss.columnCell}>
              {current} | {expected}
              <Icon
                className={scss.columnCell_icon}
                name={getCapacityIcon(current, expected)}
                size={getCapacityIcon(current, expected) === "minus" ? "sm" : undefined}
              />
            </Text>
          );
        }

        return (
          <span className={scss.columnCell}>
            <Icon name={current === "0" ? "closed-eye" : "open-eye"} />
            |
            <Icon name={expected === "0" ? "closed-eye" : "open-eye"} />
          </span>
        );
      },
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "196px" },
      },
    },
    {
      key: "responsible",
      header: t("group-management.table.responsible"),
      renderCell: (groupChange: GroupChange) => {
        if (groupChange?.username) {
          return getUserCell({ username: groupChange?.username });
        }
        return getColumnCell("-");
      },
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "196px" },
      },
    },
    {
      key: "state",
      header: t("group-management.table.state.title"),
      renderCell: (groupChange: GroupChange) => {
        const translation =
          {
            SUCCESS: t("group-management.table.state.success"),
            FAILED: t("group-management.table.state.failed"),
            PENDING: t("group-management.table.state.pending"),
          }[groupChange?.status] ?? "-";
        return getPillCell(translation);
      },
      desktopOptions: {
        headCellStyles: { minWidth: "80px", width: "130px" },
      },
    },
  ];

  // Share column definitions with parent component on mount for skeleton loading
  useEffect(() => {
    setColumns?.(columns);
  }, []);

  return (
    <section className={scss.changeRequestsTable}>
      <Table columns={columns as TableColumn[]} data={groupChanges} />
      {/* Pagination with defensive handling of potentially undefined values */}
      {!!pageInfo?.total && (
        <Pager
          classname={{ global: scss.changeRequestsTable_pagerContainer }}
          page={pageInfo?.page ?? 1}
          total={pageInfo?.total ?? 0}
          size={pageSize}
          onChange={requestGroupCapacityChangeDetails}
          onRowsPerPageChange={(size: { value: number }) => onChangeSizePerPage?.(size.value)}
          showRowsPerPage={!!onChangeSizePerPage}
          pageSizeOptions={[5, 10, 20]}
          labelRowsPerPage={t("group-management.pager.label-rows-per-page")}
        />
      )}
    </section>
  );
};

export default ChangeRequestsTable;
